import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators, ICommunicationResponse, ICoachLogEntryResponse } from '@nkr/store/index';
import { IBaseResponse, IFullImage, IMicrositeRecipient, IMicrositeRecipientCommunication, IMicrositeRecipientImage } from '@nkr/models/index';
import { CustomSiteStatus, MicrositeRecipientStatus, PhoneType, UserGroup } from '@nkr/enums/index';
import { Alert, Button, ButtonToolbar, FormControl, Glyphicon, Well } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Layout from '../layouts/v2/layout';
import Communication from './communication';
import LogList from './log/AuditLog';
import MaskedInput from 'react-maskedinput';
import axios from 'axios';
import ConfirmDiscardChanges from './confirm-discard-changes';

const ExpandableHeader = ({ onClick, expanded, children }) => {
    return (
        <span style={{ cursor: 'pointer' }} onClick={onClick} title={expanded ? 'Show less details' : 'Show more details'}>
            <strong>{children}</strong> <Glyphicon glyph={expanded ? 'chevron-up' : 'chevron-down'} />
            <br />
        </span>
    );
}

interface IProps {
    micrositeId: number
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number) => Promise<IMicrositeResponse>,
    sendNote: (micrositeRecipientId: number, note: string) => Promise<ICommunicationResponse>,
    replyToNote: (noteId: number, reply: string) => Promise<ICommunicationResponse>,
    markViewed: (micrositeRecipientId: number) => Promise<IBaseResponse>,
    updateMicrosite: (data: IMicrositeRecipient) => Promise<IBaseResponse>,
    saveCoachLogEntry: (id: number, micrositeRecipientId: number, content: string, actionType: number, isPinned: boolean) => Promise<ICoachLogEntryResponse>,
    deleteCoachLogEntry: (id: number) => Promise<IBaseResponse>,
    saveMicrositeByCoach: (formData: FormData) => Promise<IBaseResponse>,
    setMostRecentContactDate: (id: number, date: string) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    microsite: IMicrositeRecipient,
    story: string,
    why: string,
    fullImages: IFullImage[],
    pendingChanges: boolean,
    saving: boolean,
    discarding: boolean,
    updateMostRecentContactDate: {
        show: boolean,
        value: string
    },
    showConfirmDiscardChanges: boolean,
    headerDetailExpanded: boolean
}

class MicrositeView extends React.Component<TProps, IState> {
    readonly state: IState = {
        microsite: null,
        story: '',
        why: '',
        fullImages: [],
        pendingChanges: false,
        saving: false,
        discarding: false,
        updateMostRecentContactDate: {
            show: false,
            value: ''
        },
        showConfirmDiscardChanges: false,
        headerDetailExpanded: false
    }

    toggleHeaderDetail = () => {
        this.setState({
            headerDetailExpanded: !this.state.headerDetailExpanded
        })
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            Promise.all([
                this.props.markViewed(this.props.micrositeId),
                this.props.getMicrosite(this.props.micrositeId)
            ]).then( async([_, response]) => {
                if (response.data.success) {
                    let { currentStatusId, agreedToHipaa, patientStoryPending, patientWhyPending, mostRecentCoachContactDateTime, comms, imagesPending } = response.data.results;
                    let mostRecentContactDate = this.getMostRecentContactDate(comms, mostRecentCoachContactDateTime);
                    let showStarterSite = (agreedToHipaa && currentStatusId < MicrositeRecipientStatus.Active && currentStatusId != MicrositeRecipientStatus.PendCenterConfirmIsPatient) || currentStatusId == 0;
                    let fullImages = !showStarterSite ? await this.createFiles(imagesPending) : [];

                    if (fullImages.length == 0) {
                        fullImages.push({
                            caption: '',
                            data: null,
                            thumbnailData: null,
                            rotate: 0,
                            zoom: 1,
                            position: {
                                x: 0,
                                y: 0
                            }
                        });
                    }

                    this.setState({
                        microsite: response.data.results,
                        story: patientStoryPending,
                        why: patientWhyPending,
                        fullImages,
                        saving: false,
                        updateMostRecentContactDate: {
                            show: false,
                            value: mostRecentContactDate
                        }
                    });
                }
                this.props.hideLoading();
            });

        });
    }

    save = () => {
        let { microsite, story, why, fullImages } = this.state;
        let { id, editKey } = microsite

        let formData = new FormData();

        formData.append('id', id.toString());
        formData.append('editKey', editKey);
        formData.append('story', story);
        formData.append('why', why);

        let images: { index: number, caption: string, zoom: number, rotate: number, position: { x: number, y: number } }[] = [];

        let imageIndex = 0;

        fullImages.forEach((image, index) => {
            images.push({
                index,
                caption: image.caption,
                zoom: image.zoom,
                rotate: image.rotate,
                position: image.position
            });

            formData.append('thumbnails[' + imageIndex + ']', image.thumbnailData, `image-${index}-thumbnail.png`);
            formData.append('images[' + imageIndex + ']', image.data, `image-${index}.png`);

            imageIndex++;
        });

        formData.append("imageAttributes", JSON.stringify(images));

        this.setState({
            saving: true
        }, () => {
            this.props.showLoading('Updating microsite, one moment please...').then(() => {
                this.props.saveMicrositeByCoach(formData).then(response => {
                    if (response.data.success) {
                        toast.success('Microsite updated successfully');

                        this.getData();
                    }
                });
            });
        })
    }

    getMostRecentContactDate = (comms: IMicrositeRecipientCommunication[], mostRecentCoachContactDateTime: string) => {
        let coachComms = comms.filter(x => x.sentByRecipient == false);
        let d1 = coachComms.length > 0 ? moment(coachComms[0].sendDateTime, 'MM/DD/YYYY HH:mm') : null;
        let d2 = mostRecentCoachContactDateTime != '' ? moment(mostRecentCoachContactDateTime, 'MM/DD/YYYY HH:mm') : null;

        if (d1 == null && d2 != null) {
            return d2.format('MM/DD/YYYY HH:mm');
        }

        if (d1 != null && d2 == null) {
            return d1.format('MM/DD/YYYY HH:mm');
        }

        if (d1 != null && d2 != null) {
            if (d1.isBefore(d2)) {
                return d2.format('MM/DD/YYYY HH:mm');
            } else {
                return d1.format('MM/DD/YYYY HH:mm');
            }
        }

        return '';
    }

    allowEdit = () => {
        let allowCustomSiteEditList = [
            CustomSiteStatus.PendCtrDeclinedFixable,
            CustomSiteStatus.PendNkrDeclinedFixable,
            MicrositeRecipientStatus.StarterSiteActive,
            MicrositeRecipientStatus.Active
        ];

        let allowCurrentSiteEditList = [
            MicrositeRecipientStatus.StarterSiteActive,
            MicrositeRecipientStatus.Active
        ];

        let allowEdit = allowCustomSiteEditList.indexOf(this.state.microsite.currentStatusId) >= 0 || (this.state.microsite.currentStatusId == MicrositeRecipientStatus.InvitationSent && this.state.microsite.agreedToHipaa);

        return allowEdit;
    }

    prepareThumbnail = (blob: string) => {
        let jpegFile64 = blob.replace(/^data:image\/(png|jpeg);base64,/, "");
        let jpegBlob = this.base64ToBlob(jpegFile64, 'image/png');

        return jpegBlob;
    }

    base64ToBlob = (base64: string, mime: string) => {
        mime = mime || '';

        let sliceSize = 1024;
        let byteChars = window.atob(base64);
        let byteArrays = [];

        for (let offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
            let slice = byteChars.slice(offset, offset + sliceSize);

            let byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            let byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: mime });
    }

    createFiles = async (images: IMicrositeRecipientImage[]) => {
        let fullImages: IFullImage[] = [];

        for (let image of images) {
            let x = await axios.get(__API__ + '/microsite/image?filename=' + image.fileName, { responseType: 'blob' })
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    console.log(error);
                });

            let x2 = await axios.get(__API__ + '/microsite/image?filename=' + image.thumbnail, { responseType: 'blob' })
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    console.log(error);
                });

            let fullImage = {
                caption: image.caption != null ? image.caption : '',
                data: x != undefined ? new File([x], image.fileName) : null,
                thumbnailData: x2 != undefined ? new File([x2], image.thumbnail) : null,
                rotate: image.attributes != null ? image.attributes.rotate : 0,
                zoom: image.attributes != null ? image.attributes.zoom : 1,
                position: image.attributes != null ? image.attributes.position : { x: 0, y: 0 },
                thumbnail: image.thumbnail,
                fileName: image.fileName
            }

            fullImages.push(fullImage);
        }

        return fullImages;
    }

    handleReturn = () => {
        let href = '/microsite/coach';

        let parsed = qs.parse(window.location.search);

        if (parsed.previous && parsed.previous == 'ALL') {
            href += '/all';
        }

        if (parsed.previous && parsed.previous == 'TODO') {
            href += '/to-do';
        }

        window.location.href = href;
    }

    handleSendNote = (text: string, isReply: boolean, itemId: number) => {
        let { id } = this.state.microsite;

        let message = isReply ? `Sending your reply to ${this.state.microsite.fullName}, one moment please` : `Sending a note to ${this.state.microsite.fullName}, one moment please...`;
        let request = isReply ? this.props.replyToNote(itemId, text) : this.props.sendNote(id, text);

        this.props.showLoading(message).then(() => {
            request.then(response => {
                this.props.hideLoading();

                if (response.data.success) {
                    let comms = response.data.results;

                    let microsite = _.cloneDeep(this.state.microsite);
                    microsite.comms = comms;

                    let coachComms = comms.filter(x => x.sentByRecipient == false);

                    let mostRecentContactDate = this.getMostRecentContactDate(comms, microsite.mostRecentCoachContactDateTime);

                    this.setState({
                        microsite,
                        updateMostRecentContactDate: {
                            show: false,
                            value: mostRecentContactDate
                        }
                    }, () => {
                        toast.success(isReply ? 'Reply sent successfully' : 'New note sent successfully');
                    });
                }
            })
        });
    }

    handleToggleMostRecentContactDate = (show: boolean) => {
        this.setState({
            updateMostRecentContactDate: { ...this.state.updateMostRecentContactDate, show }
        });
    }

    handleUpdateMostRecentContactDate = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            updateMostRecentContactDate: { ...this.state.updateMostRecentContactDate, value: e.currentTarget.value }
        });
    }

    handleSaveMostRecentContactDate = () => {
        this.setState({
            updateMostRecentContactDate: { ...this.state.updateMostRecentContactDate, show: false },
            microsite: { ...this.state.microsite, mostRecentCoachContactDateTime: this.state.updateMostRecentContactDate.value }
        }, () => {
            this.props.showLoading('Saving recent contact date, one moment please...').then(() => {
                this.props.setMostRecentContactDate(this.state.microsite.id, this.state.updateMostRecentContactDate.value).then(response => {
                    if (response.data.success) {
                        toast.success('Recent contact date updated successfully');
                    }

                    this.props.hideLoading();
                });
            });
        });
    }

    handleSaveLogEntry = (id: number, micrositeRecipientId: number, content: string, actionType: number, isPinned: boolean) => {
        this.props.showLoading('Saving personal log entry, one moment please...').then(() => {
            this.props.saveCoachLogEntry(id, this.state.microsite.id, content, actionType, isPinned).then(response => {
                if (response.data.success) {
                    let logEntries = _.cloneDeep(this.state.microsite.coachLogEntries);

                    if (id == 0) {
                        logEntries.push(response.data.results);
                    } else {
                        let { updatedDateTime, displayDate, content } = response.data.results;

                        logEntries = logEntries.map(note => {
                            if (note.id == id) {
                                note.updatedDateTime = updatedDateTime;
                                note.displayDate = displayDate;
                                note.content = content;
                            }

                            return note;
                        });
                    }

                    this.setState({
                        microsite: {...this.state.microsite, coachLogEntries: logEntries}
                    }, () => {
                        toast.success('Personal log updated successfully');
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    handleDeleteLogEntry = (id: number) => {
        this.props.showLoading('Deleting personal log entry, one moment please...').then(() => {
            this.props.deleteCoachLogEntry(id).then(response => {
                if (response.data.success) {
                    let logEntries = _.cloneDeep(this.state.microsite.coachLogEntries).filter(x => x.id != id);

                    this.setState({
                        microsite: {...this.state.microsite, coachLogEntries: logEntries}
                    }, () => {
                        toast.success('Personal log updated successfully');
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    handleChangeImages = (fullImages: IFullImage[]) => {
        this.setState({
            fullImages
        });
    }

    handleCommitImages = (fullImages: IFullImage[]) => {
        this.setState({
            fullImages,
            pendingChanges: true
        });
    }

    handleCommitStory = (story: string) => {
        let pendingChanges = this.state.microsite.patientStoryPending != story;

        this.setState({
            story,
            pendingChanges
        });
    }

    handleCommitWhy = (why: string) => {
        let pendingChanges = this.state.microsite.patientWhyPending != why;

        this.setState({
            why,
            pendingChanges
        });
    }

    handleConfirmDiscardChanges = () => {
        this.setState({
            showConfirmDiscardChanges: true
        });
    }

    handleDiscardChangesYes = async () => {
        let fullImages = await this.createFiles(this.state.microsite.imagesPending);

        if (fullImages.length == 0) {
            fullImages.push({
                caption: '',
                data: null,
                thumbnailData: null,
                rotate: 0,
                zoom: 1,
                position: {
                    x: 0,
                    y: 0
                }
            });
        }

        this.setState({
            story: this.state.microsite.patientStoryPending,
            why: this.state.microsite.patientWhyPending,
            fullImages,
            showConfirmDiscardChanges: false,
            pendingChanges: false,
            discarding: true
        }, () => {
            this.setState({
                discarding: false
            })
        });
    }

    handleDiscardChangesNo = () => {
        this.setState({
            showConfirmDiscardChanges: false
        });
    }

    render = () => {
        if (this.state.microsite == null)
            return null;

        const { microsite, story, why, fullImages } = this.state;
        const { shortCode, firstName, lastName, age, fullName } = microsite;

        const profile = fullImages.find((x, i) => i == 0);
        const images = fullImages.filter((x, i) => i > 0);

        const i = this.state.microsite.imagesPending

        const returnTitle = this.props.app.user.groupId == UserGroup.Guest && this.props.app.user.isMicrositeCoach ? 'Return to Your List' : 'Return to the List';

        const isInactive = [
            MicrositeRecipientStatus.Inactive,
            MicrositeRecipientStatus.Denied,
            MicrositeRecipientStatus.InvitationSent,
            MicrositeRecipientStatus.PendCenterConfirmIsPatient
        ].indexOf(this.state.microsite.currentStatusId) >= 0;

        const showStarterSite = (
            microsite.agreedToHipaa
            && microsite.currentStatusId < MicrositeRecipientStatus.Active
            && microsite.currentStatusId != MicrositeRecipientStatus.PendCenterConfirmIsPatient)
                || microsite.currentStatusId == 0;

        return (
            <>
                <div className='container-fluid'>
                    <div className='row' style={{ marginTop: 10, marginBottom: 30 }}>
                        <div className='col-xs-12 col-lg-12'>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <h1 style={{ margin: 0 }}>Microsite for {fullName}</h1>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Button bsStyle='link' onClick={this.handleReturn}>{returnTitle}</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <Well bsSize='sm'>
                        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'top' }}>
                            
                            <div style={{ textAlign: 'center' }}>
                                <strong>Type</strong><br />
                                {this.state.microsite.micrositeType}
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <strong>Status</strong><br />
                                {this.state.microsite.currentStatusDescription}
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <ExpandableHeader onClick={this.toggleHeaderDetail} expanded={this.state.headerDetailExpanded}>Patient</ExpandableHeader>
                                {this.state.microsite?.fullName && <>{this.state.microsite?.fullName}</>}
                                {this.state.headerDetailExpanded &&
                                    <>
                                        <br />
                                        {this.state.microsite?.phonePrimary && <>{this.state.microsite?.phonePrimary}<br /></>}
                                        {this.state.microsite?.phoneSecondary && <>{this.state.microsite?.phoneSecondary}<br /></>}
                                        {this.state.microsite?.email && <>{this.state.microsite?.email}<br /></>}
                                        {this.state.microsite?.secondaryEmail && <>{this.state.microsite?.secondaryEmail}<br /></>}
                                    </>
                                }
                                
                            </div>
                            
                            <div style={{ textAlign: 'center' }}>
                                <ExpandableHeader onClick={this.toggleHeaderDetail} expanded={this.state.headerDetailExpanded}>Patient Location</ExpandableHeader>
                                {this.state.headerDetailExpanded &&
                                    <>
                                        {this.state.microsite?.addressLine1 && <>{this.state.microsite?.addressLine1}<br /></>}
                                        {(this.state.microsite?.city || this.state.microsite?.state) && <>{[this.state.microsite?.city, this.state.microsite?.state].filter(Boolean).join(', ')}<br /></>}
                                        {this.state.microsite?.postalCode && <>{this.state.microsite?.postalCode}</>}
                                    </>
                                }
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <ExpandableHeader onClick={this.toggleHeaderDetail} expanded={this.state.headerDetailExpanded}>Center/Coordinator</ExpandableHeader>
                                {this.state.microsite?.center?.alias && <>{this.state.microsite?.center?.alias}</>}
                                {this.state.headerDetailExpanded &&
                                    <>
                                        <br />
                                        {this.state.microsite?.center?.name && <>{this.state.microsite?.center?.name}<br /></>}
                                        {this.state.microsite?.coordinatorContact?.fullName && <>{this.state.microsite?.coordinatorContact?.fullName}<br /></>}
                                        {this.state.microsite?.coordinatorContact?.phoneNumbers.filter(f => f.phoneTypeId == PhoneType.Office).map(p => <>{p.number} ({p.phoneTypeName})<br /></>)}
                                        {this.state.microsite?.coordinatorContact?.email && <>{this.state.microsite?.coordinatorContact?.email}<br /></>}
                                    </>
                                }
                                
                            </div>

                            {!this.state.updateMostRecentContactDate.show ?
                                <div style={{ cursor: 'pointer' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <strong>Most Recent Contact Date/Time</strong><br />
                                        {this.state.updateMostRecentContactDate.value != '' ?
                                            <span onClick={() => this.handleToggleMostRecentContactDate(true)}>{this.state.updateMostRecentContactDate.value} <Glyphicon glyph='pencil' /></span>
                                            :
                                            <span onClick={() => this.handleToggleMostRecentContactDate(true)}>[Click to update]</span>
                                        }
                                    </div>
                                </div>
                                :
                                <>
                                    <div>
                                        <div style={{ textAlign: 'center' }}>
                                            <strong>Most&nbsp;Recent&nbsp;Contact&nbsp;Date/Time</strong><br />
                                            <div style={{ display: 'flex' }}>
                                                <MaskedInput
                                                    className='form-control'
                                                    mask={'11/11/1111 11:11'}
                                                    value={this.state.updateMostRecentContactDate.value}
                                                    onBlur={this.handleUpdateMostRecentContactDate}
                                                />
                                                <Button bsStyle='primary' bsSize='sm' style={{ marginRight: 5 }} onClick={this.handleSaveMostRecentContactDate}><Glyphicon glyph='ok' /></Button>
                                                <Button bsSize='sm' onClick={() => this.handleToggleMostRecentContactDate(false)}><Glyphicon glyph='remove' /></Button>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }

                        </div>
                    </Well>

                    {showStarterSite && (
                        <Alert bsStyle='highlight' bsSize='sm'>This site is currently a starter site and can't be edited.</Alert>
                    )}

                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                        {(this.state.saving == false && this.state.discarding == false) && (
                            <div className={'microsites ' + this.state.microsite.micrositeType.toLowerCase()} style={{ width: '60%' }}>
                                <Layout
                                    id={this.state.microsite.id}
                                    micrositeTypeId={this.state.microsite.micrositeTypeId}
                                    micrositeType={this.state.microsite.micrositeType}
                                    shortCode={shortCode}
                                    firstName={firstName}
                                    lastName={lastName}
                                    age={age}
                                    waitListStartDate={this.state.microsite.waitListStartDate}
                                    transplantUrgency={this.state.microsite.transplantUrgency}
                                    story={story}
                                    why={why != '' ? why : `Hello! My name is ${this.state.microsite.firstName} ${this.state.microsite.lastName} and I need a ${this.state.microsite.micrositeType.toLowerCase()} transplant.
                        
                                    Please consider being tested to determine if you are healthy enough to donate on my behalf.
                                    
                                    To determine if you are medically able to donate, click the <strong>See if you're qualified to donate</strong> button.
                                    
                                    Thank you for viewing my web site.`}
                                    profile={profile}
                                    images={this.state.microsite.images}
                                    previewImages={[]}
                                    starterSite={showStarterSite}
                                    location={2}
                                    isIE={false}
                                    isEditable={false}
                                    sendToLivingDonation={() => null}
                                    onImageClick={() => null}
                                    onCommitStory={this.handleCommitStory}
                                    onCommitWhy={this.handleCommitWhy}
                                />
                            </div>
                        )}

                        <div style={{ width: '40%', paddingLeft: 10, paddingRight: 10 }}>
                            <Communication
                                list={this.state.microsite.comms}
                                recipientName={fullName}
                                recipientActive={!isInactive}
                                onSendNote={this.handleSendNote}
                            />

                            <LogList
                                data={this.state.microsite}
                                onSaveCoachLogEntry={this.handleSaveLogEntry}
                                onDeleteLogEntry={this.handleDeleteLogEntry}
                            />
                        </div>
                    </div>
                </div>

                <ConfirmDiscardChanges
                    show={this.state.showConfirmDiscardChanges}
                    onYes={this.handleDiscardChangesYes}
                    onNo={this.handleDiscardChangesNo}
                />
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrosite: (id) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, true));
        },
        sendNote: (micrositeRecipientId: number, note: string) => {
            return dispatch(MicrositeActionCreators.sendNote(micrositeRecipientId, note));
        },
        replyToNote: (noteId: number, reply: string) => {
            return dispatch(MicrositeActionCreators.replyToNote(noteId, reply));
        },
        markViewed: (micrositeRecipientId: number) => {
            return dispatch(MicrositeActionCreators.markViewed(micrositeRecipientId));
        },
        updateMicrosite: (data) => {
            return dispatch(MicrositeActionCreators.updateMicrosite(data));
        },
        saveCoachLogEntry: (id, micrositeRecipientId, content, actionType, isPinned) => {
            return dispatch(MicrositeActionCreators.saveCoachLogEntry(id, micrositeRecipientId, content, actionType, isPinned));
        },
        deleteCoachLogEntry: (id) => {
            return dispatch(MicrositeActionCreators.deleteCoachLogEntry(id));
        },
        saveMicrositeByCoach: (formData) => {
            return dispatch(MicrositeActionCreators.saveMicrositeByCoach(formData));
        },
        setMostRecentContactDate: (id, date) => {
            return dispatch(MicrositeActionCreators.setMostRecentContactDate(id, date));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeView);